import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { resumeData } from "../data/resume";

const CAPTURE_SCALE = 2;
const CAPTURE_WINDOW_WIDTH = 1280; // force desktop layout in the capture
const IMAGE_TIMEOUT_MS = 20000;
const INK = [43, 47, 122];
const MUTED = [107, 110, 120];
const BORDER = [200, 202, 214];
const TRACK = [227, 228, 234];

/** Ordinal suffix for a day-of-month, e.g. 1 -> "st", 22 -> "nd". */
function ordinalSuffix(day) {
  const onesDigit = day % 10;
  const lastTwoDigits = day % 100;
  if (onesDigit === 1 && lastTwoDigits !== 11) return "st";
  if (onesDigit === 2 && lastTwoDigits !== 12) return "nd";
  if (onesDigit === 3 && lastTwoDigits !== 13) return "rd";
  return "th";
}

/** Timestamped filename in UTC, e.g. "Trent Roddan Resume 15th Aug 2026 6.28am UTC.pdf". */
export function buildResumeFileName(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(now);

  const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  const day = parseInt(get("day"), 10);
  const ampm = get("dayPeriod").toLowerCase().replace(/\./g, "");
  const suffix = ordinalSuffix(day);

  return `Trent Roddan Resume ${day}${suffix} ${get("month")} ${get("year")} ${get("hour")}.${get("minute")}${ampm} UTC.pdf`;
}

/**
 * Capture the resume sheet as a canvas, forcing the desktop (two-column)
 * layout and aligning columns so CEO Alignment stays whole on page 1.
 * Returns the canvas plus the y-positions (canvas px) of forced page breaks.
 */
async function captureSheet(node) {
  let breakTopsCss = [];

  const canvas = await html2canvas(node, {
    scale: CAPTURE_SCALE,
    useCORS: true,
    backgroundColor: "#ffffff",
    width: node.offsetWidth,
    windowWidth: CAPTURE_WINDOW_WIDTH,
    imageTimeout: IMAGE_TIMEOUT_MS,
    ignoreElements: (el) => el.classList && el.classList.contains("pdf-exclude"),
    onclone: (doc, element) => alignColumnsAndMeasure(doc, element, (tops) => {
      breakTopsCss = tops;
    }),
  });

  const breakYs = breakTopsCss.map((t) => t * CAPTURE_SCALE).sort((a, b) => a - b);
  return { canvas, breakYs };
}

/** Insert an invisible spacer so the left column lines up with the right, then measure break markers. */
function alignColumnsAndMeasure(doc, element, setTops) {
  const coreEl = element.querySelector(".pdf-corecap");
  const profExpEl = element.querySelector(".pdf-profexp");

  if (coreEl && profExpEl) {
    const top0 = element.getBoundingClientRect().top;
    const diff =
      (profExpEl.getBoundingClientRect().top - top0) -
      (coreEl.getBoundingClientRect().top - top0);
    if (diff > 4 && diff < 200) {
      const spacer = doc.createElement("div");
      spacer.style.height = `${diff}px`;
      spacer.setAttribute("aria-hidden", "true");
      coreEl.parentNode.insertBefore(spacer, coreEl);
    }
  }

  const sheetTop = element.getBoundingClientRect().top;
  const tops = Array.from(element.querySelectorAll(".pdf-break-before"))
    .map((el) => el.getBoundingClientRect().top - sheetTop)
    .filter((t) => t > 0);
  setTops(tops);
}

/** Page geometry + pixel data needed for slicing. */
function computeGeometry(canvas) {
  const A4W = 595.28;
  const A4H = 841.89;
  const margin = 24;
  const contentWpt = A4W - margin * 2;
  const contentHpt = A4H - margin * 2;
  const cW = canvas.width;
  const cH = canvas.height;
  const pxPerPt = cW / contentWpt;
  const pageHpx = Math.floor(contentHpt * pxPerPt);
  const data = canvas.getContext("2d").getImageData(0, 0, cW, cH).data;
  return { A4W, A4H, margin, contentWpt, contentHpt, cW, cH, pxPerPt, pageHpx, data };
}

/** True when a canvas row is entirely near-white (a safe place to cut). */
function isBlankRow(data, cW, y) {
  const rowStart = y * cW * 4;
  for (let x = 0; x < cW; x += 6) {
    const i = rowStart + x * 4;
    if (data[i] < 244 || data[i + 1] < 244 || data[i + 2] < 244) return false;
  }
  return true;
}

/** Decide where the current page should end. */
function chooseCutPoint(start, geom, breakYs) {
  const { cH, pageHpx, data, cW } = geom;
  let end = Math.min(start + pageHpx, cH);

  const topSkip = pageHpx * 0.06;
  const forced = breakYs
    .filter((y) => y > start + topSkip && y < end)
    .sort((a, b) => a - b)[0];
  if (forced !== undefined) return Math.round(forced);

  if (end < cH) {
    const lookback = Math.floor(pageHpx * 0.18);
    for (let y = end; y > end - lookback && y > start + 40; y--) {
      if (isBlankRow(data, cW, y)) return y;
    }
  }
  return end;
}

/** Slice the tall canvas into A4 pages, honouring forced breaks. */
function paginate(pdf, canvas, breakYs, geom) {
  const { cW, cH, margin, contentWpt, pxPerPt } = geom;
  let start = 0;
  let first = true;

  while (start < cH) {
    const end = chooseCutPoint(start, geom, breakYs);
    const sliceHpx = end - start;

    const tmp = document.createElement("canvas");
    tmp.width = cW;
    tmp.height = sliceHpx;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, cW, sliceHpx);
    tctx.drawImage(canvas, 0, start, cW, sliceHpx, 0, 0, cW, sliceHpx);

    if (!first) pdf.addPage();
    pdf.addImage(tmp.toDataURL("image/jpeg", 0.95), "JPEG", margin, margin, contentWpt, sliceHpx / pxPerPt, undefined, "FAST");
    first = false;
    start = end;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Append a page of clickable YouTube thumbnails. */
async function appendVideosPage(pdf, videos, geom) {
  if (!videos || !videos.length) return;
  const { A4H, margin, contentWpt } = geom;

  pdf.addPage();
  let y = margin + 6;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(...INK);
  pdf.text("FEATURED VIDEOS", margin, y + 6);
  y += 14;
  pdf.setDrawColor(...INK);
  pdf.setLineWidth(2);
  pdf.line(margin, y, margin + contentWpt, y);
  y += 16;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text("Click any thumbnail to watch the video on YouTube.", margin, y);
  y += 14;

  const cols = 2;
  const gap = 16;
  const cellW = (contentWpt - gap) / cols;
  const thumbH = (cellW * 9) / 16;
  const rowH = thumbH + 16 + 12;

  for (let i = 0; i < videos.length; i++) {
    const col = i % cols;
    const x = margin + col * (cellW + gap);

    if (col === 0 && y + rowH > A4H - margin) {
      pdf.addPage();
      y = margin + 6;
    }

    const v = videos[i];
    try {
      pdf.addImage(await loadImage(v.thumb), "JPEG", x, y, cellW, thumbH, undefined, "FAST");
    } catch (_err) {
      pdf.setFillColor(...TRACK);
      pdf.rect(x, y, cellW, thumbH, "F");
    }
    pdf.setDrawColor(...BORDER);
    pdf.setLineWidth(0.5);
    pdf.rect(x, y, cellW, thumbH);
    pdf.link(x, y, cellW, thumbH, { url: `https://www.youtube.com/watch?v=${v.id}` });

    if (col === cols - 1) y += rowH;
  }
}

/** Build the full multi-page A4 resume PDF and trigger the download. */
export async function generateAndSaveResumePdf(node) {
  const { canvas, breakYs } = await captureSheet(node);
  const geom = computeGeometry(canvas);
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

  paginate(pdf, canvas, breakYs, geom);
  await appendVideosPage(pdf, resumeData.videos, geom);
  pdf.save(buildResumeFileName());
}
